import { Controller } from "@hotwired/stimulus"

const RADAR_CONFIG = {
  c3: {
    axesRadius: 100,
    dataRadius: 90,
    axes: [
      { key: "gsc", label: "Symptom Severity", mobileLabel: "Symptoms", mode: "gsc" },
      { key: "maps", label: "Processing Speed", mobileLabel: "Processing", mode: "higher_better" },
      { key: "trails_diff", label: "Trails B-A", mode: "trails_diff" },
      { key: "crt", label: "Choice RT", mode: "lower_better" },
      { key: "srt", label: "Simple RT", mode: "lower_better" },
      { key: "bess_evaluation_errors", label: "Balance", mode: "bess" }
    ]
  },
  mental_health: {
    axesRadius: 100,
    dataRadius: 90,
    axes: [
      { key: "apsq", label: "APSQ", mode: "lower_better" },
      { key: "anxiety", label: "Anxiety", mode: "lower_better" },
      { key: "depression", label: "Depression", mode: "lower_better" },
      { key: "sleep", label: "Sleep", mode: "lower_better" },
      { key: "alcohol", label: "Alcohol", mode: "lower_better" },
      { key: "eating_disorder", label: "Eating", mobileLabel: "Eating", mode: "lower_better" }
    ]
  }
}

const GSC_MAX = 162
const BESS_ERRORS_MAX = 60

export default class extends Controller {
  static values = {
    data: Object
  }

  connect() {
    if (!window.echarts || !this.hasDataValue) return

    this.buildStructure()
    this.chart = window.echarts.init(this.chartElement)
    this.legendSelection = { ...(this.dataValue.initial_selected || {}) }
    this.activeReferenceName = this.dataValue.active_reference || null
    this.renderChart()
    this.handleResize = () => {
      if (!this.chart) return
      this.renderChart()
      this.chart.resize()
    }
    window.addEventListener("resize", this.handleResize)
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.legendElement?.removeEventListener("click", this.handleLegendClick)
    this.chart?.dispose()
    this.chart = null
  }

  renderChart() {
    const config = RADAR_CONFIG[this.dataValue.selected_radar]
    const assessments = this.dataValue.assessments || []
    if (!config || assessments.length === 0) return

    const references = this.referenceAssessments(assessments)
    const reference = this.activeReference(references)
    if (!reference) return

    const axes = config.axes.filter((axis) => this.present(reference[axis.key]))
    if (axes.length < 3) {
      this.chartElement.innerHTML = "<p class='my-chart__empty'>Not enough chart data is available yet.</p>"
      this.legendElement.innerHTML = ""
      return
    }

    const chartSeries = []
    const selected = this.buildSelectedState(assessments, reference)
    this.activeReferenceName = reference.description
    const compactLayout = this.useCompactLayout(assessments.length)

    assessments.forEach((assessment) => {
      if (this.referenceAssessment(assessment)) {
        chartSeries.push(this.referenceSeries(assessment, axes, config))
        return
      }

      if (selected[assessment.description] === false) {
        chartSeries.push(this.dataSeries(assessment, Array(axes.length).fill(0)))
        return
      }

      const values = axes.map((axis) => this.normalizeValue(axis, assessment[axis.key], reference[axis.key], config))
      if (values.every((value) => value == null)) return

      chartSeries.push(this.dataSeries(assessment, values.map((value) => value == null ? 0 : value)))
    })

    this.chart.setOption({
      animationDuration: 350,
      color: ["#64748b", "#2e6fb3", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"],
      tooltip: {
        trigger: "item",
        confine: true
      },
      legend: this.legendOptions(selected, compactLayout),
      radar: {
        center: compactLayout ? ["50%", "49%"] : ["28%", "56%"],
        radius: compactLayout ? "50%" : "48%",
        clockwise: true,
        splitNumber: 4,
        min: 0,
        max: config.axesRadius,
        indicator: axes.map((axis) => ({
          name: compactLayout ? (axis.mobileLabel || axis.label) : axis.label,
          max: config.axesRadius
        })),
        splitArea: {
          areaStyle: {
            color: ["rgba(46, 111, 179, 0.03)", "rgba(46, 111, 179, 0.06)"]
          }
        },
        axisName: {
          color: "#16324f",
          fontSize: compactLayout ? 10 : 12
        }
      },
      series: [
        {
          type: "radar",
          data: chartSeries
        }
      ]
    })

    this.renderCompactLegend(assessments, selected, compactLayout)
    this.chart.off("legendselectchanged")
    this.chart.on("legendselectchanged", (event) => this.handleLegendSelect(event, assessments))
  }

  legendOptions(selected, compactLayout) {
    if (compactLayout) {
      return {
        show: false,
        selected
      }
    }

    return {
      type: "scroll",
      orient: "vertical",
      right: "2%",
      top: "12%",
      bottom: "12%",
      width: "30%",
      selected,
      textStyle: {
        color: "#16324f"
      }
    }
  }

  useCompactLayout(assessmentCount) {
    const width = this.element.clientWidth || window.innerWidth || 0
    return width < 860 || assessmentCount > 5
  }

  buildStructure() {
    this.element.innerHTML = ""
    this.element.classList.add("my-chart-shell")

    this.chartElement = document.createElement("div")
    this.chartElement.className = "my-chart__canvas"

    this.legendElement = document.createElement("div")
    this.legendElement.className = "my-chart__chips"
    this.legendElement.hidden = true

    this.element.append(this.chartElement, this.legendElement)

    this.handleLegendClick = (event) => {
      const chip = event.target.closest("[data-chart-chip]")
      if (!chip) return

      const name = chip.dataset.name
      const isReference = chip.dataset.reference === "true"
      if (!name) return

      if (isReference) {
        this.activeReferenceName = name
        this.renderChart()
        this.chart?.resize()
        return
      }

      const currentlySelected = this.legendSelection[name] !== false
      this.legendSelection = {
        ...this.legendSelection,
        [name]: !currentlySelected
      }
      this.renderChart()
      this.chart?.resize()
    }

    this.legendElement.addEventListener("click", this.handleLegendClick)
  }

  renderCompactLegend(assessments, selected, compactLayout) {
    if (!compactLayout) {
      this.legendElement.innerHTML = ""
      this.legendElement.hidden = true
      return
    }

    this.legendElement.hidden = false
    this.legendElement.innerHTML = ""

    assessments.forEach((assessment, index) => {
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "my-chart__chip"
      chip.dataset.chartChip = "true"
      chip.dataset.name = assessment.description
      chip.dataset.reference = this.referenceAssessment(assessment) ? "true" : "false"
      chip.dataset.active = selected[assessment.description] === false ? "false" : "true"

      if (assessment.description === this.activeReferenceName) {
        chip.dataset.currentReference = "true"
      }

      const seriesColor = this.seriesColor(index)
      chip.innerHTML = `
        <span class="my-chart__chip-swatch" style="--chip-color: ${seriesColor};"></span>
        <span class="my-chart__chip-label">${this.compactLabel(assessment.description)}</span>
      `
      this.legendElement.appendChild(chip)
    })
  }

  compactLabel(name) {
    return name
      .replace("Normative Data", "Norms")
      .replace("Baseline:", "BL:")
      .replace("Follow Up:", "FU:")
  }

  seriesColor(index) {
    const colors = ["#64748b", "#2e6fb3", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"]
    return colors[index % colors.length]
  }

  normalizeValue(axis, currentValue, referenceValue, config) {
    if (!this.present(currentValue) || !this.present(referenceValue)) return null

    const current = Number(currentValue)
    const reference = Number(referenceValue)
    if (Number.isNaN(current) || Number.isNaN(reference)) return null

    const radius = this.legacyRadius(axis.mode, current, reference, config.dataRadius)
    if (radius == null || Number.isNaN(radius)) return null

    return Math.max(0, Math.min(config.axesRadius, radius))
  }

  referenceSeries(assessment, axes, config) {
    return {
      name: assessment.description,
      value: axes.map(() => config.dataRadius),
      lineStyle: {
        width: 2,
        color: "#64748b",
        type: assessment.baseline ? "dashed" : "solid"
      },
      areaStyle: { color: "rgba(100, 116, 139, 0.08)" },
      symbol: "none"
    }
  }

  dataSeries(assessment, values) {
    return {
      name: assessment.description,
      value: values,
      lineStyle: {
        width: assessment.description === "90% Baseline" ? 2 : 3,
        type: assessment.description === "90% Baseline" ? "dashed" : "solid"
      },
      areaStyle: {
        opacity: assessment.description === "90% Baseline" ? 0.03 : 0.14
      },
      symbol: "circle",
      symbolSize: 8
    }
  }

  handleLegendSelect(event, assessments) {
    const clickedAssessment = assessments.find((assessment) => assessment.description === event.name)
    if (!clickedAssessment) return

    if (this.referenceAssessment(clickedAssessment)) {
      if (event.selected[event.name]) {
        this.activeReferenceName = clickedAssessment.description
      }

      this.legendSelection = {
        ...event.selected,
        ...this.referenceSelectionFor(this.activeReferenceName || clickedAssessment.description, assessments)
      }
      this.renderChart()
      return
    }

    this.legendSelection = { ...event.selected }
  }

  buildSelectedState(assessments, activeReference) {
    const defaults = {}

    assessments.forEach((assessment) => {
      defaults[assessment.description] = this.referenceAssessment(assessment) ? assessment.description === activeReference.description : true
    })

    return {
      ...defaults,
      ...this.legendSelection,
      ...this.referenceSelectionFor(activeReference.description, assessments)
    }
  }

  referenceSelectionFor(activeReferenceName, assessments) {
    return assessments
      .filter((assessment) => this.referenceAssessment(assessment))
      .reduce((selection, assessment) => {
        selection[assessment.description] = assessment.description === activeReferenceName
        return selection
      }, {})
  }

  referenceAssessments(assessments) {
    return assessments.filter((assessment) => this.referenceAssessment(assessment))
  }

  activeReference(references) {
    return references.find((assessment) => assessment.description === this.activeReferenceName) ||
      references.find((assessment) => assessment.baseline) ||
      references[0]
  }

  referenceAssessment(assessment) {
    return assessment.description === "Normative Data" || assessment.baseline || assessment.best_ever
  }

  legacyRadius(mode, assessment, baseline, dataRadius) {
    let radius

    if (mode === "gsc") {
      if (GSC_MAX - baseline > (GSC_MAX - assessment) * 2) {
        radius = 0
      } else {
        radius = Math.round((2 - ((GSC_MAX - baseline) / (GSC_MAX - assessment))) * dataRadius)
      }
    } else if (mode === "bess") {
      if (BESS_ERRORS_MAX - baseline > (BESS_ERRORS_MAX - assessment) * 2) {
        radius = 0
      } else {
        radius = Math.round((2 - ((BESS_ERRORS_MAX - baseline) / (BESS_ERRORS_MAX - assessment))) * dataRadius)
      }
    } else if (mode === "higher_better") {
      if (baseline > assessment * 2) {
        radius = 0
      } else {
        radius = Math.round((2 - (baseline / assessment)) * dataRadius)
      }
    } else if (mode === "trails_diff") {
      radius = this.legacyTrailsDiffRadius(assessment, baseline, dataRadius)
    } else {
      if (assessment > baseline * 2) {
        radius = 0
      } else {
        radius = Math.round((2 - (assessment / baseline)) * dataRadius)
      }
    }

    if (radius == null || Number.isNaN(radius)) return null
    return radius
  }

  legacyTrailsDiffRadius(assessment, baseline, dataRadius) {
    if (baseline < 0) {
      if (assessment < 0) return Math.round((baseline / assessment) * dataRadius)
      return 0
    }

    if (baseline > 0) {
      if (assessment < 0) return Math.round(2 * dataRadius)
      if (assessment > 0) return Math.round((baseline / assessment) * dataRadius)
      return Math.round(2 * dataRadius)
    }

    if (assessment < 0) return Math.round(2 * dataRadius)
    if (assessment > 0) return 0
    return Math.round(dataRadius)
  }

  present(value) {
    return value != null && value !== "no data"
  }
}
